import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MapPin, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { JobsPageSEO } from "../components/SEO";
import "./JobsPage.css";
import { supabase } from "../lib/supabase";

export default function JobsPage() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('is_active', true)
        .order('posted_date', { ascending: false });

      if (error) throw error;

      // Format dates for display
      const formattedJobs = data.map(job => ({
        ...job,
        posted: formatPostedDate(job.posted_date)
      }));

      setJobs(formattedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - fetchJobs doesn't depend on any props or state

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const formatPostedDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Posted today";
  if (diffDays === 1) return "Posted yesterday";
  if (diffDays < 7) return `Posted ${diffDays} days ago`;
  if (diffDays < 30) return `Posted ${Math.floor(diffDays / 7)} weeks ago`;

  return date.toLocaleDateString();
};


  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (typeFilter !== "All" && j.type !== typeFilter) return false;
      if (!q) return true;
      return (
        j.title.toLowerCase().includes(q) ||
        j.location.toLowerCase().includes(q) ||
        j.description.toLowerCase().includes(q)
      );
    });
  }, [jobs, search, typeFilter]);

  const handleApply = (jobId) => {
    navigate(`/apply/${jobId}`);
  };

  if (loading) {
    return (
      <div className="jobs-root">
        <header className="site-header small">
          <div className="container header-inner"></div>
        </header>
        <main className="container jobs-main">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading jobs...
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="jobs-root">
      <JobsPageSEO />
      <header className="site-header small">
        <div className="container header-inner"></div>
      </header>

      <main className="container jobs-main">
        <h2 className="page-title">Available Positions</h2>

        <div className="controls">
          <input
            aria-label="Search jobs"
            className="search-input"
            placeholder="Search by title, location or keyword"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="select-input"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option>All</option>
            <option>Full-time</option>
            <option>Part-time</option>
            <option>Contract</option>
            <option>Internship</option>
          </select>
        </div>

        <section className="jobs-list">
          {filteredJobs.length === 0 && (
            <div className="empty">No positions match your search.</div>
          )}

          {filteredJobs.map((job) => (
            <article key={job.id} className="job-card">
              <div className="job-main">
                <div>
                  <h3 className="job-title">{job.title}</h3>
                  <div className="meta">
                    <span className="meta-item">
                      <MapPin className="meta-icon" /> {job.location}
                    </span>
                    <span className="meta-item badge">{job.type}</span>
                    <span className="meta-item muted">{job.posted}</span>
                  </div>
                </div>

                <div className="job-actions">
                  <button
                    className="btn-details"
                    onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                  >
                    {expanded === job.id ? "Hide" : "Details"}
                  </button>
                  <button
                    className="btn-apply"
                    onClick={() => handleApply(job.id)}
                    title={`Apply for ${job.title}`}
                  >
                    Apply <ChevronRight className="chev" />
                  </button>
                </div>
              </div>

              {expanded === job.id && (
                <div className="job-details">
                  <h4>Role Details</h4>
                  <p>{job.description}</p>
                  <p className="muted">
                    <em>To apply, click the Apply button above.</em>
                  </p>
                </div>
              )}
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}